import { useMemo } from 'react';
import { useBrand } from '@/context/BrandContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { processShortcodes, containsShortcodes, extractShortcodes, getAvailableShortcodes } from '@/lib/shortcodes';

/**
 * Custom hook for using shortcodes in components
 * @returns Object with shortcode processing functions and data
 */
export function useShortcodes() {
  const { state } = useBrand();

  const { currentWorkspace } = useWorkspace();

  // Process shortcodes in text
  const processText = useMemo(() => {
    return (text: string): string => {
      return processShortcodes(text, state.brandData, currentWorkspace?.name || 'AgencyPlus');
    };
  }, [state.brandData, currentWorkspace?.name]);

  // Check if text contains shortcodes
  const hasShortcodes = useMemo(() => {
    return (text: string): boolean => {
      return containsShortcodes(text);
    };
  }, []);

  // Extract shortcodes from text
  const getShortcodes = useMemo(() => {
    return (text: string): string[] => {
      return extractShortcodes(text);
    };
  }, []);

  // Get available shortcodes
  const availableShortcodes = useMemo(() => {
    return getAvailableShortcodes();
  }, []);

  // Get brand data for shortcode replacement
  const getBrandData = useMemo(() => {
    return () => ({
      brandName: state.brandData?.name || 'Brand Name',
      brandUrl: state.brandData?.brandUrl || 'brand.com',
      publishedDate: (() => {
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const year = now.getFullYear();
        return `${month}-${day}-${year}`;
      })(),
      designCredit: state.brandData?.designCredit || currentWorkspace?.name || 'AgencyPlus',
      brandLogo: state.brandData?.brandLogo || ''
    });
  }, [state.brandData]);

  return {
    processText,
    hasShortcodes,
    getShortcodes,
    availableShortcodes,
    getBrandData,
    brandData: state.brandData
  };
}
